/* eslint-disable react/require-default-props */
import React from 'react';
import { OverlayDrawer, useFluent } from '@fluentui/react-components';

import {
  useDrawerStackContext,
  computeStackOffset,
  computeStackZIndex,
} from './drawer-stack-context';

// Returns focus to whatever was focused before this drawer opened.
//
// Fluent restores focus through tabster's `restorer`, which only fires when the
// opener carries `useModalAttributes().triggerAttributes`. Stack consumers render
// controlled drawers with no `DrawerTrigger`, so no restore target is ever
// registered — on close tabster finds nothing to restore to and focus falls back
// to `<body>`, stranding keyboard users outside the drawer left underneath.
// AB#11062710.
const useRestoreFocusOnClose = (enabled: boolean): void => {
  const { targetDocument } = useFluent();

  // Layout effect so the capture happens before Fluent's focus-first-element
  // effect moves focus into the drawer.
  React.useLayoutEffect(() => {
    if (!enabled) return undefined;

    const doc = targetDocument ?? document;
    const previouslyFocused = doc.activeElement as HTMLElement | null;

    return () => {
      // Queued before tabster's own restore timer, which then no-ops because
      // focus is no longer on <body>.
      setTimeout(() => {
        if (doc.activeElement !== doc.body) return;
        if (!previouslyFocused || previouslyFocused === doc.body) return;
        if (!previouslyFocused.isConnected) return;
        previouslyFocused.focus();
      }, 0);
    };
  }, [enabled, targetDocument]);
};

// We use `React.ComponentProps<typeof OverlayDrawer>` rather than Fluent's
// exported `OverlayDrawerProps` because the exported type is narrower (e.g.
// it constrains `onOpenChange`'s data type) and forces additional generic
// plumbing on consumers. Note: `as` is widened to `"aside" | "div"` here
// while the actual component only accepts `"aside"`, so we Omit it — the
// drawer's element type is not configurable by stack consumers.
type OverlayDrawerComponentProps = React.ComponentProps<typeof OverlayDrawer>;
type OverlayDrawerPassthroughProps = Omit<
  OverlayDrawerComponentProps,
  'as' | 'open' | 'position' | 'size' | 'modalType' | 'style' | 'className' | 'onOpenChange' | 'children'
>;

export interface StackedDrawerProps extends OverlayDrawerPassthroughProps {
  /** Unique name identifying this drawer in the stack (must match a name in openDrawers) */
  name: string;
  /** Called when open state changes (e.g. backdrop click, Escape key) */
  onOpenChange?: OverlayDrawerComponentProps['onOpenChange'];
  /** Additional className for the drawer */
  className?: string;
  /** Additional inline styles for the drawer */
  style?: React.CSSProperties;
  /**
   * Restore focus to the element that opened this drawer when it closes.
   * Set to `false` when the consumer manages focus itself. Defaults to `true`.
   */
  restoreFocusOnClose?: boolean;
  /** Children (DrawerHeader, DrawerBody, DrawerFooter, etc.) */
  children: React.ReactNode;
}

const OpenStackedDrawer: React.FC<StackedDrawerProps> = ({
  name,
  children,
  className,
  style,
  onOpenChange,
  restoreFocusOnClose = true,
  ...overlayDrawerProps
}) => {
  const ctx = useDrawerStackContext();

  useRestoreFocusOnClose(restoreFocusOnClose);

  const offset = computeStackOffset(name, ctx.openDrawers, ctx.offsetPx);
  const zIndex = computeStackZIndex(name, ctx.openDrawers);

  return (
    <OverlayDrawer
      {...overlayDrawerProps}
      position="end"
      size="medium"
      open
      modalType="modal"
      className={className}
      style={{
        width: ctx.drawerWidth,
        right: `${offset}px`,
        transition: `right ${ctx.animationDurationMs}ms ease`,
        zIndex,
        ...style,
      }}
      onOpenChange={onOpenChange}
    >
      {children}
    </OverlayDrawer>
  );
};

export const StackedDrawer: React.FC<StackedDrawerProps> = (props) => {
  const ctx = useDrawerStackContext();

  // Split so the focus-restore hook only exists while the drawer is open: some
  // consumers keep StackedDrawer mounted when closed (including during SSR).
  if (!ctx.openDrawers.includes(props.name)) return null;

  return <OpenStackedDrawer {...props} />;
};
